import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/transfert/chauffeur-transfert.png";
import { cityPriceFaq } from "./cityPricing";

const beausoleil: ChauffeurPage = {
  slug: "beausoleil",
  title: "Chauffeur privé à Beausoleil – VTC 24h/24",
  subtitle:
    "Chauffeur privé et VTC à Beausoleil : la ville au-dessus de Monte-Carlo, sa frontière-rue avec Monaco, ses immeubles Belle Époque et le transfert aéroport de Nice en ~35 min.",
  keywords: [
    "chauffeur privé beausoleil",
    "vtc beausoleil",
    "chauffeur beausoleil monaco",
    "transfert beausoleil aéroport nice",
  ],
  hero: {
    title: "Chauffeur privé & VTC à Beausoleil",
    subtitle:
      "Le balcon français au-dessus de Monte-Carlo : desserte de Monaco à pied ou en voiture, dans une ville en pente à l’héritage Belle Époque.",
    keywords: [
      "VTC Beausoleil",
      "chauffeur Monte-Carlo",
      "transfert aéroport Nice",
      "chauffeur Belle Époque",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Chauffeur privé & VTC à Beausoleil",
    badges: [
      "Au-dessus de Monte-Carlo",
      "Immeubles Belle Époque",
      "~35 min de l’aéroport",
    ],
  },
  benefits: {
    heading: "Pourquoi un chauffeur à Beausoleil",
    items: [
      {
        title: "Au-dessus de Monte-Carlo",
        description:
          "La frontière suit ici une rue : le chauffeur vous dépose à la limite de la Principauté, d’où Monte-Carlo se rejoint à pied, sans plonger dans la circulation monégasque.",
      },
      {
        title: "Rues en pente & Belle Époque",
        description:
          "Ville verticale aux fortes déclivités, escaliers et ascenseurs publics : un chauffeur habitué aux montées raides jusqu’au pied de votre immeuble.",
      },
      {
        title: "Balcon sur la Principauté",
        description:
          "Depuis les hauteurs de Beausoleil, Monaco se déploie en contrebas : liaisons courtes vers Monte-Carlo, le Rocher et Fontvieille.",
      },
      {
        title: "À ~35 minutes de l’aéroport",
        description:
          "Transfert direct depuis et vers l’aéroport de Nice, par la moyenne corniche ou l’A8, avec suivi de vol.",
      },
    ],
  },
  services: {
    heading: "Nos services de chauffeur à Beausoleil",
    services: [
      {
        title: "Desserte de Monaco",
        text: "Dépose à la frontière ou au cœur de Monte-Carlo pour vos rendez-vous, sans gérer le stationnement dans la Principauté.",
      },
      {
        title: "Transfert aéroport de Nice",
        text: "Beausoleil ⇄ aéroport de Nice en ~35 min, avec suivi de vol et accueil personnalisé.",
      },
      {
        title: "Navettes en ville haute",
        text: "Prise en charge au pied des immeubles en pente et des rues en escaliers, jusqu’au bas de la ville ou à Monaco.",
      },
      {
        title: "Événements de Monaco",
        text: "Beausoleil surplombe le circuit : pendant le Grand Prix et les galas, navettes et dépose au plus près des accès.",
      },
      {
        title: "Mise à disposition & corniches",
        text: "À l’heure ou à la journée pour relier Monaco, Cap-d’Ail, Èze et Nice par les corniches.",
      },
    ],
  },
  locations: {
    heading: "Lieux & points de prise en charge",
    items: [
      {
        name: "Boulevard de France",
        description:
          "Rue-frontière entre Beausoleil et Monaco, un trottoir de chaque pays.",
      },
      {
        name: "Riviera Palace",
        description:
          "Immeuble Belle Époque et sa verrière, emblème de la ville.",
      },
      {
        name: "Ascenseurs vers Monte-Carlo",
        description: "Accès public reliant la ville haute au niveau de Monaco.",
      },
      {
        name: "Place de la Libération",
        description: "Centre animé de Beausoleil, commerces et marché.",
      },
      {
        name: "Rues en escaliers",
        description:
          "Montées et venelles entre les niveaux de la ville en pente.",
      },
      {
        name: "Moyenne Corniche",
        description: "Route en surplomb reliant Nice, Èze et Monaco.",
      },
    ],
  },
  generic: {
    heading: "Un chauffeur pour le balcon de Monte-Carlo",
    subheading:
      "Beausoleil grimpe au-dessus de Monaco, où la frontière tient dans une rue et la ville s’étage en pente.",
    paragraphs: [
      "Accrochée au versant qui domine Monte-Carlo, Beausoleil partage avec Monaco une frontière si ténue qu’elle suit le milieu d’une rue. On y descend à la Principauté à pied ou par les ascenseurs publics ; pour le reste, un chauffeur absorbe les fortes pentes et vous dépose au plus près, en haut comme en bas de la ville.",
      "KS Rent Services applique des forfaits fixes annoncés à l’avance — dont Beausoleil ⇄ aéroport de Nice — et la mise à disposition à l’heure ou à la journée, avec devis immédiat. Nos chauffeurs bilingues français/anglais, basés sur la Côte d’Azur, sont notés 5,0/5 sur 38 avis Google.",
      "Les immeubles Belle Époque, à l’image du Riviera Palace, rappellent l’âge d’or de la station ; aujourd’hui, Beausoleil sert surtout de point d’appui au-dessus de Monaco, d’où le chauffeur rejoint Monte-Carlo, le Rocher ou Fontvieille en quelques minutes.",
    ],
    bullets: [
      "Dépose à la frontière, Monte-Carlo à pied",
      "Forfaits fixes & mise à disposition, devis immédiat",
      "Chauffeurs bilingues FR/EN, 5,0/5 sur 38 avis Google",
    ],
  },
  process: {
    heading: "Réserver son chauffeur à Beausoleil",
    steps: [
      "Indiquez votre programme : Monaco, transfert aéroport, navette en ville haute ou événement.",
      "Nous confirmons le véhicule et le forfait ou la mise à disposition.",
      "Le chauffeur vous prend en charge au pied de votre immeuble, même dans les rues en pente.",
      "Ajustements en direct selon le trafic et les accès à Monaco.",
    ],
  },
  faq: [
    cityPriceFaq("fr", "Beausoleil"),
    {
      q: "Combien de temps pour rejoindre l’aéroport de Nice ?",
      a: "Environ 35 minutes par la moyenne corniche ou l’A8. Le chauffeur suit votre vol et adapte l’heure de départ selon le trafic à l’approche de Nice.",
    },
    {
      q: "Comment se passe la desserte de Monaco depuis Beausoleil ?",
      a: "La frontière longe une rue de Beausoleil : nous vous déposons à la limite pour rejoindre Monte-Carlo à pied, ou nous vous conduisons directement au cœur de la Principauté selon votre rendez-vous.",
    },
    {
      q: "Intervenez-vous pendant le Grand Prix de Monaco ?",
      a: "Oui. Beausoleil surplombe le circuit ; pendant le Grand Prix et les galas, nous organisons des navettes et déposons au plus près des accès malgré les fermetures.",
    },
    {
      q: "Pouvez-vous venir jusqu’au pied des immeubles en pente ?",
      a: "Oui. Nos chauffeurs connaissent les montées raides et les rues en escaliers de Beausoleil et viennent vous prendre au plus près de votre entrée.",
    },
    {
      q: "Vos chauffeurs parlent-ils anglais ?",
      a: "Oui, nos chauffeurs sont bilingues français/anglais, habitués aux résidents et visiteurs qui rejoignent quotidiennement Monaco.",
    },
    {
      q: "Quels véhicules proposez-vous pour Beausoleil ?",
      a: "Berline Mercedes, van 7 places ou SUV selon le nombre de passagers, à l’aise dans les rues pentues de la ville haute.",
    },
  ],
  relatedLinks: [
    { label: "Voiture avec chauffeur à Monaco", href: "/chauffeur/monaco" },
    {
      label: "Aéroport de Nice, tarifs et trajets",
      href: "/chauffeur/aeroport",
    },
    { label: "Voiture avec chauffeur à Nice", href: "/chauffeur/nice" },
    {
      label: "Service de chauffeur sur la Riviera",
      href: "/chauffeur/cote-d-azur",
    },
  ],
  cta: {
    title: "Votre chauffeur privé à Beausoleil",
    text: "Desserte de Monaco, transfert aéroport ou navette en ville haute : réservez votre chauffeur.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver / demander un devis",
  },
  seo: {
    title: "Chauffeur privé à Beausoleil 24h/24 — VTC & transferts",
    description:
      "Chauffeur privé et VTC à Beausoleil : au-dessus de Monte-Carlo, desserte de Monaco, immeubles Belle Époque et transfert aéroport de Nice en ~35 min.",
    canonical: "/chauffeur/beausoleil",
  },
};

export default beausoleil;
